import { Dispatch, Fragment, ReactNode, SetStateAction } from 'react';
import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';
import { Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { MdArrowForward, MdCheckBox, MdInfoOutline } from 'react-icons/md';

import {
  PlanRow,
  RowEdit,
  TYPES_TO_NAMES,
  ISCI
} from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Checkbox } from '@Components/FormCheck';
import { Button } from '@Components/Button';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { convert24hrTo12hr } from '@src/utils/time-utils';
import { DATE_FORMAT } from '@src/utils/date-utils';
import { useMap } from '@src/utils/hooks/useData';
import { PRETTY_EDIT_NAMES } from '../linear-buying-constants';
import { ExtendEditModalData, EditsMap } from '../linear-buying-utils';
import './ExtendEditModal.scss';

interface GenericEditDetailProps {
  oldValue: ReactNode;
  newValue: ReactNode;
  selectedEdit: string | null;
}

const GenericEditDetail = ({ oldValue, newValue, selectedEdit }: GenericEditDetailProps) => (
  <>
    <span className='edit-type'>{selectedEdit ? PRETTY_EDIT_NAMES[selectedEdit] : ''}:</span>
    <span className='old-value'>{oldValue}</span>
    <MdArrowForward />
    <span className='new-value'>{newValue}</span>
  </>
);

interface CreativeEditDetailProps {
  oldRotation: Record<string, number>;
  newRotation: Record<string, number>;
}

const CreativeEditDetail = ({ oldRotation, newRotation }: CreativeEditDetailProps) => (
  <>
    {Object.keys(newRotation).map(isci => {
      const pct = newRotation[isci];
      const unchanged = newRotation[isci] === oldRotation[isci];
      return (
        <Fragment key={isci}>
          <span>{isci}</span>
          <span className={unchanged ? 'unchanged' : 'old-value'}>
            {oldRotation[isci] ? `${oldRotation[isci]}%` : '0%'}
          </span>
          <MdArrowForward />
          <span className={unchanged ? 'unchanged' : 'new-value'}>{`${pct}%`}</span>
        </Fragment>
      );
    })}
  </>
);

interface ExtendEditModalProps {
  onClose: () => void;
  editData: ExtendEditModalData;
  rowOptions: PlanRow[];
  setEditsMap: Dispatch<SetStateAction<EditsMap>>;
}

export const ExtendEditModal = ({
  onClose,
  editData,
  rowOptions,
  setEditsMap
}: ExtendEditModalProps) => {
  const [selectedRows, setSelectedRowValue] = useMap<string, boolean>({});

  const { row, edits, selectedEditToExtend } = editData;
  const { week, network, daypart_start, daypart_end, dow, avail, length, type, cost, notes } = row;

  const oldValue = row[selectedEditToExtend!];
  const newValue = edits[selectedEditToExtend!];

  const oldRotation: Record<string, number> = {};
  const newRotation: Record<string, number> = {};
  if (selectedEditToExtend === 'creatives') {
    for (const creativeMetadata of edits[selectedEditToExtend]!) {
      const { isci, percent } = creativeMetadata;
      newRotation[isci] = percent;
    }
    if (row.creatives) {
      for (const creativeMetadata of row.creatives) {
        const { isci, percent } = creativeMetadata;
        oldRotation[isci] = percent;
      }
    }
  }

  const checkAll = () => {
    for (const rowOption of rowOptions) {
      setSelectedRowValue(rowOption.key, true);
    }
  };

  const addSelected = () => {
    setEditsMap(current => {
      const selected: EditsMap = {};
      for (const rowOption of rowOptions) {
        if (selectedRows[rowOption.key]) {
          // If we are extending an edit to creatives, we need to update the plan_id field to the id
          // that matches the row we are extending into. Otherwise, all future weeks will have the
          // original plan_id in the creatives rows
          if (selectedEditToExtend === 'creatives') {
            const creativesWithCorrectPlanId: ISCI[] = edits.creatives!.map(creative => ({
              ...creative,
              plan_id: rowOption.plan_id
            }));
            selected[rowOption.key] = {
              ...rowOption,
              edits: {
                ...(R.path([rowOption.key, 'edits'], current) as RowEdit),
                [selectedEditToExtend]: creativesWithCorrectPlanId
              }
            };
          } else {
            selected[rowOption.key] = {
              ...rowOption,
              edits: {
                ...(R.path([rowOption.key, 'edits'], current) as RowEdit),
                [selectedEditToExtend!]: edits[selectedEditToExtend!]
              }
            };
          }
        }
      }

      return { ...current, ...selected };
    });
    onClose();
  };

  return (
    <Modal show onHide={onClose} size='lg' className='extend-edit-modal'>
      <Modal.Header closeButton>
        <Modal.Title className='modal-title'>
          <div className='title'>Extend Selected Edit</div>
          <OverlayTrigger
            placement={Placement.RIGHT_START}
            overlay={
              <Tooltip id='tooltip'>
                <p>
                  Displays all future weeks containing a matching plan to that of the selected edit.
                </p>
              </Tooltip>
            }
          >
            <MdInfoOutline />
          </OverlayTrigger>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='header'>
          <div className='week'>{R.pipe(Dfns.parseISO, Dfns.format(DATE_FORMAT))(week)}</div>
          <div className='selected-row-details'>
            <div className='detail'>
              <span className='label'>Network: </span>
              <span>{network}</span>
            </div>
            <div className='detail'>
              <span className='label'>Avail: </span>
              <span>{avail}</span>
            </div>
            <div className='detail'>
              <span className='label'>Start time: </span>
              <span>{convert24hrTo12hr(daypart_start)}</span>
            </div>
            <div className='detail'>
              <span className='label'>End time: </span>
              <span>{convert24hrTo12hr(daypart_end)}</span>
            </div>
            <div className='detail'>
              <span className='label'>Days: </span>
              <span>{dow.join(',')}</span>
            </div>
            <div className='detail'>
              <span className='label'>Type: </span>
              <span>{TYPES_TO_NAMES[`${type}`]}</span>
            </div>
            <div className='detail'>
              <span className='label'>Length: </span>
              <span>{length}</span>
            </div>
            <div className='detail'>
              <span className='label'>Cost: </span>
              <span>{formatMoney(cost, 0)}</span>
            </div>
            <div className='detail'>
              <span className='label'>Notes: </span>
              <span>{notes}</span>
            </div>
          </div>
          <div className='edit-details'>
            {selectedEditToExtend === 'creatives' ? (
              <CreativeEditDetail oldRotation={oldRotation} newRotation={newRotation} />
            ) : (
              <GenericEditDetail
                oldValue={oldValue}
                newValue={newValue}
                selectedEdit={selectedEditToExtend}
              />
            )}
          </div>
        </div>
        {rowOptions.length ? (
          <div className='future-rows'>
            <Button
              size={Size.LG}
              className='check-all-button'
              appearance={Appearance.OUTLINED}
              leadingElement={<MdCheckBox />}
              onClick={checkAll}
            >
              Check All
            </Button>
            <Table striped bordered>
              <thead>
                <tr>
                  <th className='select' aria-label='Select row' />
                  <th className='company'>Week</th>
                  <th className='notes'>Edit</th>
                </tr>
              </thead>
              <tbody>
                {rowOptions.map(rowOption => (
                  <tr key={rowOption.key}>
                    <td className='checkbox'>
                      <Checkbox
                        checked={Boolean(selectedRows[rowOption.key])}
                        onChange={() =>
                          setSelectedRowValue(rowOption.key, !selectedRows[rowOption.key])
                        }
                      />
                    </td>
                    <td className='week'>
                      {R.pipe(Dfns.parseISO, Dfns.format(DATE_FORMAT))(rowOption.week)}
                    </td>
                    <td className='edit'>
                      {selectedEditToExtend === 'creatives' ? (
                        <CreativeEditDetail oldRotation={oldRotation} newRotation={newRotation} />
                      ) : (
                        <GenericEditDetail
                          oldValue={oldValue}
                          newValue={newValue}
                          selectedEdit={selectedEditToExtend}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div>No matching rows.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={addSelected}>Add Selected</Button>
      </Modal.Footer>
    </Modal>
  );
};
